/**
 * batch-49 in-te-plannen (FO-398) — pure regels achter het vak "In te plannen".
 *
 * Alles wat de urgentie, de sortering en de herkomst-tekst van een onbemande
 * dienst bepaalt, staat hier: DB-vrij, één bron voor de API (band-DTO, escalatie-
 * cron, dekkingsmail) én de backoffice-UI. Zonder deze module zou de "< 48 uur is
 * rood"-regel op drie plekken los geïmplementeerd worden en gegarandeerd uit de
 * pas lopen.
 *
 * Tijdzone: dit rekent met absolute momenten (ms), niet met kalenderdagen — een
 * dienst over 47 uur is urgent, ongeacht in welke NL-dag die valt. De dienst-
 * STARTTIJD is echter NL-wandkloktijd ("08:00" betekent 08:00 in Amsterdam), dus
 * die wordt via `amsterdamWallclockToUtc` naar het absolute moment omgezet - hem
 * als UTC lezen zou structureel 1-2 uur schelen op `urgent`/`verlopen` en de
 * escalatie-cron.
 */

import { amsterdamWallclockToUtc } from './amsterdam-tijd'

/** §14 A4 — instelbaar gebouwd, met deze defaults. */
export const IN_TE_PLANNEN_URGENT_UREN = 48
/** "Te lang open": item ligt langer dan dit aantal dagen in het vak (§7.5). */
export const IN_TE_PLANNEN_TE_LANG_OPEN_DAGEN = 7

const UUR_MS = 3_600_000
const DAG_MS = 86_400_000

export type InTePlannenHerkomstKey =
  | 'UITDIENST'
  | 'UITDIENST_OPSCHONING'
  | 'LOSGEKOPPELD'
  | 'ZIEKTE'
  | 'VERLOF'
  | 'NIEUW'

/**
 * Uren tot de start van de dienst. Negatief = de dienst ligt in het verleden
 * (§7.1 "verlopen"). `plannedStart` is "HH:mm" in NL-dienst-tijd op de dag van
 * `datum` (UTC-middernacht, zoals elke dagregel).
 */
export function urenTotDienst(datum: Date, plannedStart: string, nu: Date): number {
  // `datum` is UTC-middernacht (de dagregel-conventie), dus de kalenderdag lezen
  // we uit de UTC-componenten. De tijd erbij is NL-wandklok.
  const ymd = datum.toISOString().slice(0, 10)
  const hm = /^\d{1,2}:\d{2}$/.test(plannedStart) ? plannedStart : '00:00'
  const start = amsterdamWallclockToUtc(ymd, hm)
  return (start.getTime() - nu.getTime()) / UUR_MS
}

/**
 * < 48 uur tot uitvoering → rode markering (§3.1) en escalatie (§7.5). Een
 * dienst die al gepasseerd is telt NIET als urgent maar als "verlopen": daar is
 * de actie herplannen of annuleren, niet "snel iemand zoeken voor vanmiddag".
 */
export function isDienstUrgent(urenTot: number, drempelUren = IN_TE_PLANNEN_URGENT_UREN): boolean {
  return urenTot >= 0 && urenTot < drempelUren
}

/** Dienst ligt in het verleden (§7.1) — no-show door vertrek, "verlopen"-markering. */
export function isDienstVerlopen(urenTot: number): boolean {
  return urenTot < 0
}

/** Ligt het item te lang in het vak? (§7.5, tweede escalatiegrond.) */
export function isTeLangOpen(
  ontkoppeldOp: Date | null,
  nu: Date,
  drempelDagen = IN_TE_PLANNEN_TE_LANG_OPEN_DAGEN,
): boolean {
  if (!ontkoppeldOp) return false
  return nu.getTime() - ontkoppeldOp.getTime() >= drempelDagen * DAG_MS
}

/** Prioriteit → rangorde (hoger = urgenter). Onbekend/leeg telt als NORMAAL. */
const PRIORITEIT_RANG: Record<string, number> = {
  KRITIEK: 3,
  HOOG: 2,
  NORMAAL: 1,
  LAAG: 0,
}

export function prioriteitRang(prioriteit: string | null | undefined): number {
  return PRIORITEIT_RANG[prioriteit ?? 'NORMAAL'] ?? 1
}

export interface InTePlannenSorteerbaar {
  /** Uren tot de eerste onbemande dienst (negatief = verleden). */
  urenTotEersteDienst: number
  prioriteit: string | null
  /** ISO-datum van de eerste onbemande dag; tiebreak zodat de volgorde stabiel is. */
  eersteDatum: string
  werkbonId: string
}

/**
 * §3.1 sortering: "eerst op urgentie (dagen tot de eerste dienst, dan
 * prioriteit)".
 *
 * Verlopen diensten (verleden) staan bovenaan: daar is al iets misgegaan, dus
 * die vragen als eerste een besluit. Daarna oplopend op tijd-tot-dienst, en bij
 * gelijke tijd de hoogste prioriteit eerst. Werkbon-id als laatste tiebreak
 * zodat twee identieke items nooit van plek wisselen tussen renders.
 */
export function vergelijkInTePlannen(
  a: InTePlannenSorteerbaar,
  b: InTePlannenSorteerbaar,
): number {
  const aVerlopen = isDienstVerlopen(a.urenTotEersteDienst)
  const bVerlopen = isDienstVerlopen(b.urenTotEersteDienst)
  if (aVerlopen !== bVerlopen) return aVerlopen ? -1 : 1
  if (a.urenTotEersteDienst !== b.urenTotEersteDienst) {
    return a.urenTotEersteDienst - b.urenTotEersteDienst
  }
  const rang = prioriteitRang(b.prioriteit) - prioriteitRang(a.prioriteit)
  if (rang !== 0) return rang
  if (a.eersteDatum !== b.eersteDatum) return a.eersteDatum < b.eersteDatum ? -1 : 1
  return a.werkbonId < b.werkbonId ? -1 : a.werkbonId > b.werkbonId ? 1 : 0
}

/**
 * §13.7 kopie: "Uitdienst {naam}" / "Losgekoppeld door {naam}" / "Ziekte {naam}"
 * / "Nieuw". Namen zijn optioneel — ontbreekt de naam, dan valt het label terug
 * op de kale herkomst in plaats van "Uitdienst undefined".
 */
export function herkomstLabel(
  herkomst: InTePlannenHerkomstKey,
  namen: { vanMedewerker?: string | null; doorGebruiker?: string | null } = {},
): string {
  const van = namen.vanMedewerker?.trim() || null
  const door = namen.doorGebruiker?.trim() || null
  switch (herkomst) {
    case 'UITDIENST':
      return van ? `Uitdienst ${van}` : 'Uitdienst'
    case 'UITDIENST_OPSCHONING':
      return van ? `Uitdienst ${van} (opschoning)` : 'Uitdienst (opschoning)'
    case 'ZIEKTE':
      return van ? `Ziekte ${van}` : 'Ziekte'
    case 'VERLOF':
      return van ? `Verlof ${van}` : 'Verlof'
    case 'LOSGEKOPPELD':
      return door ? `Losgekoppeld door ${door}` : 'Losgekoppeld'
    case 'NIEUW':
      return 'Nieuw'
  }
}
